import CloseIcon from '@mui/icons-material/Close';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Typography,
} from '@mui/material';
import { isEmpty, isEqual, map } from 'lodash';
import { enqueueSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Form } from 'react-final-form';
import { useDispatch } from 'react-redux';

import { setPatientContactsForPartners } from 'store/thunks/patientThunks';

import {
  buildChangedPartnerContacts,
  buildPartnerContactsFormValues,
  buildSubmitErrorsFromRejectedFields,
  findFirstRejectedFieldName,
  findUnconfirmedRemovalFieldNames,
} from '../../utils/partnerContactsForm';
import PartnerContactSection from './PartnerContactSection';
import partnerContactPropType from './partnerContactPropType';

const TITLE_ID = 'PartnerContactsDialog-title';
const SUBTITLE_ID = 'PartnerContactsDialog-subtitle';

const describeEditedPartnerCount = (editedPartnerCount) => {
  if (!editedPartnerCount) return 'No changes';

  return editedPartnerCount === 1 ? '1 partner edited' : `${editedPartnerCount} partners edited`;
};

const PartnerContactsDialog = ({ mpi, patientName, partnerContacts, onClose }) => {
  const dispatch = useDispatch();
  const [isSaving, setIsSaving] = useState(false);
  const [confirmedRemovalFieldNames, setConfirmedRemovalFieldNames] = useState([]);
  const [fieldNameToFocus, setFieldNameToFocus] = useState(null);
  const inputsByFieldName = useRef({});

  const initialValues = useMemo(
    () => buildPartnerContactsFormValues(partnerContacts),
    [partnerContacts]
  );

  const registerInput = useCallback(
    (fieldName) => (inputElement) => {
      inputsByFieldName.current[fieldName] = inputElement;
    },
    []
  );

  const focusField = useCallback((fieldName) => {
    inputsByFieldName.current[fieldName]?.focus();
  }, []);

  // Runs once the fields are enabled again after a rejected save.
  useEffect(() => {
    if (fieldNameToFocus && !isSaving) {
      focusField(fieldNameToFocus);
      setFieldNameToFocus(null);
    }
  }, [fieldNameToFocus, focusField, isSaving]);

  const confirmRemoval = useCallback((fieldName) => {
    setConfirmedRemovalFieldNames((fieldNames) => [...fieldNames, fieldName]);
  }, []);

  const handleClose = (_, reason) => {
    if (reason !== 'backdropClick' && !isSaving) {
      onClose();
    }
  };

  const handleSave = (values) => {
    const changedPartnerContacts = buildChangedPartnerContacts({
      partnerContactsOnFile: partnerContacts,
      formContacts: values.partnerContacts,
    });

    if (isSaving || isEmpty(changedPartnerContacts)) {
      return undefined;
    }

    setIsSaving(true);

    return dispatch(
      setPatientContactsForPartners({ mpi, partnerContacts: changedPartnerContacts })
    ).then(({ isSaved, rejectedFields }) => {
      setIsSaving(false);

      if (isSaved) {
        enqueueSnackbar('Partner contacts saved', { variant: 'success' });
        onClose();
        return undefined;
      }

      // Any other failure is already on screen in the shared error dialog; the edits stay.
      if (isEmpty(rejectedFields)) {
        return undefined;
      }

      const submitErrors = buildSubmitErrorsFromRejectedFields({
        partnerContactsOnFile: partnerContacts,
        rejectedFields,
      });
      setFieldNameToFocus(findFirstRejectedFieldName(submitErrors));

      return submitErrors;
    });
  };

  return (
    <Dialog
      open
      onClose={handleClose}
      maxWidth='sm'
      fullWidth
      aria-labelledby={TITLE_ID}
      aria-describedby={SUBTITLE_ID}
    >
      <Form
        initialValues={initialValues}
        initialValuesEqual={isEqual}
        onSubmit={handleSave}
        render={({
          handleSubmit,
          values,
          active,
          hasValidationErrors,
          hasSubmitErrors,
          dirtySinceLastSubmit,
          submitError,
        }) => {
          const editedPartnerCount = buildChangedPartnerContacts({
            partnerContactsOnFile: partnerContacts,
            formContacts: values.partnerContacts,
          }).length;
          const unconfirmedRemovalFieldNames = findUnconfirmedRemovalFieldNames({
            partnerContactsOnFile: partnerContacts,
            formContacts: values.partnerContacts,
            confirmedRemovalFieldNames,
          });
          // final-form keeps a server rejection in `invalid` until the next submit, so a rejected
          // save is allowed again as soon as anything changed since that submit.
          const canSave =
            !isSaving &&
            editedPartnerCount > 0 &&
            !hasValidationErrors &&
            (!hasSubmitErrors || dirtySinceLastSubmit) &&
            isEmpty(unconfirmedRemovalFieldNames);

          return (
            <form noValidate onSubmit={handleSubmit}>
              <Box sx={{ position: 'relative', px: 3, pt: 2.5, pb: 2 }}>
                <DialogTitle id={TITLE_ID} sx={{ p: 0, pr: 5 }}>
                  {`Patient contact by partner — ${patientName}`}
                </DialogTitle>
                <Typography
                  id={SUBTITLE_ID}
                  variant='body2'
                  color='text.secondary'
                  sx={{ mt: 0.5, pr: 5 }}
                >
                  Each partner notifies the patient at the email and phone under its name. All
                  changes are saved together.
                </Typography>
                <IconButton
                  aria-label='Close'
                  onClick={onClose}
                  disabled={isSaving}
                  sx={{ position: 'absolute', top: 16, right: 16 }}
                >
                  <CloseIcon />
                </IconButton>
              </Box>

              <DialogContent dividers sx={{ py: 0 }}>
                {submitError && (
                  <Alert severity='error' sx={{ mt: 2 }}>
                    <strong>Nothing was saved.</strong> {submitError}
                  </Alert>
                )}

                {isEmpty(partnerContacts) && (
                  <Typography variant='body2' color='text.secondary' sx={{ py: 2 }}>
                    No partners
                  </Typography>
                )}

                {map(partnerContacts, (partnerContactOnFile, index) => (
                  <Fragment key={partnerContactOnFile.partnerId}>
                    {index > 0 && <Divider />}
                    <PartnerContactSection
                      index={index}
                      partnerContactOnFile={partnerContactOnFile}
                      formContact={values.partnerContacts?.[index]}
                      activeFieldName={active}
                      confirmedRemovalFieldNames={confirmedRemovalFieldNames}
                      unconfirmedRemovalFieldNames={unconfirmedRemovalFieldNames}
                      isSaving={isSaving}
                      onConfirmRemoval={confirmRemoval}
                      registerInput={registerInput}
                      focusField={focusField}
                    />
                  </Fragment>
                ))}
              </DialogContent>

              <DialogActions sx={{ justifyContent: 'space-between', py: 1, px: 2 }}>
                <Typography variant='caption' color='text.secondary' sx={{ pl: 1 }}>
                  {isSaving
                    ? 'Saving all partners…'
                    : describeEditedPartnerCount(editedPartnerCount)}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button onClick={onClose} disabled={isSaving}>
                    Cancel
                  </Button>
                  <Button
                    type='submit'
                    variant='contained'
                    disabled={!canSave}
                    aria-busy={isSaving}
                    startIcon={isSaving ? <CircularProgress size={16} color='inherit' /> : null}
                    // Keeps the primary fill while saving; disabled only to stop a second submit.
                    sx={
                      isSaving
                        ? {
                            '&.Mui-disabled': {
                              bgcolor: 'primary.main',
                              color: 'primary.contrastText',
                            },
                          }
                        : undefined
                    }
                  >
                    {isSaving ? 'Saving' : 'Save'}
                  </Button>
                </Box>
              </DialogActions>
            </form>
          );
        }}
      />
    </Dialog>
  );
};

PartnerContactsDialog.propTypes = {
  mpi: PropTypes.string.isRequired,
  patientName: PropTypes.string.isRequired,
  partnerContacts: PropTypes.arrayOf(partnerContactPropType).isRequired,
  onClose: PropTypes.func.isRequired,
};

export default PartnerContactsDialog;
